import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  X,
  Loader2,
  Image as ImageIcon,
  Upload,
  Eye,
  MousePointerClick,
  MapPin,
  RefreshCcw,
  Power,
  BarChart3,
  Smartphone,
  Check,
} from 'lucide-react';
import {
  bannersAPI,
  resolveAssetUrl,
  type Banner,
  type BannerInput,
  type BannerStats,
} from '../../services/api';
import { useModalKeys } from '../../hooks/useModalKeys';

/**
 * Central > Banners — CRUD de banners promocionais do app (Fase 20c).
 *
 * - Grid de cards com preview da imagem, status, cidades e métricas
 * - Criar/editar em modal: imagem (upload ou URL), vigência, cidades
 *   (chips), página de detalhe (título/corpo/rodapé exibidos no app)
 * - Toggle ativo/inativo direto no card
 * - Modal de estatísticas: quem visualizou/clicou (cpf, cidade, device)
 */

function errorMessage(err: unknown): string {
  const e = err as { response?: { data?: { message?: string } }; message?: string };
  return e.response?.data?.message ?? e.message ?? 'Erro desconhecido';
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR');
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function maskCpf(cpf: string): string {
  if (cpf.length === 11) {
    return `${cpf.slice(0, 3)}.${cpf.slice(3, 6)}.${cpf.slice(6, 9)}-${cpf.slice(9)}`;
  }
  return cpf;
}

function toDateInput(iso: string): string {
  return iso ? iso.slice(0, 10) : '';
}

export default function BannersTab() {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Banner | 'new' | null>(null);
  const [statsFor, setStatsFor] = useState<Banner | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setBanners(await bannersAPI.list());
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleToggle(b: Banner) {
    try {
      const r = await bannersAPI.toggle(b.id);
      setBanners((list) =>
        list.map((x) => (x.id === b.id ? { ...x, isActive: r.isActive } : x)),
      );
    } catch (err) {
      alert(errorMessage(err));
    }
  }

  async function handleDelete(b: Banner) {
    if (!confirm(`Excluir o banner "${b.title}"? As métricas serão perdidas.`)) return;
    try {
      await bannersAPI.remove(b.id);
      setBanners((list) => list.filter((x) => x.id !== b.id));
    } catch (err) {
      alert(errorMessage(err));
    }
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-2">
        <p className="text-sm text-fg-2">
          {banners.length} banner(s) — o app exibe o ativo de maior prioridade
          válido pra cidade do cliente.
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 bg-canvas hover:bg-hover border border-line text-fg rounded-lg text-sm transition"
          >
            <RefreshCcw size={14} className={loading ? 'animate-spin' : ''} />
            Atualizar
          </button>
          <button
            type="button"
            onClick={() => setEditing('new')}
            className="flex items-center gap-2 px-3 py-2 bg-primary-solid hover:bg-primary-solid-hover rounded-lg text-sm font-medium text-white transition"
          >
            <Plus size={14} />
            Novo banner
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-danger-soft/20 border border-danger-line/50 text-sm text-danger">
          {error}
        </div>
      )}

      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-64 rounded-2xl bg-canvas border border-line animate-pulse" />
          ))}
        </div>
      ) : banners.length === 0 ? (
        <div className="rounded-2xl border border-line bg-surface py-16 text-center">
          <ImageIcon size={40} className="mx-auto mb-3 text-faint" />
          <p className="text-fg-2">Nenhum banner criado ainda.</p>
          <p className="text-sm text-muted">
            Crie o primeiro — ele aparece no app do cliente final.
          </p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {banners.map((b) => (
            <div
              key={b.id}
              className="rounded-2xl border border-line bg-surface overflow-hidden flex flex-col"
            >
              {/* Imagem */}
              <button
                type="button"
                onClick={() => setStatsFor(b)}
                className="relative block aspect-[2/1] bg-canvas group"
                title="Ver estatísticas"
              >
                {b.imageUrl ? (
                  <img
                    src={resolveAssetUrl(b.imageUrl) ?? b.imageUrl}
                    alt={b.title}
                    className="h-full w-full object-cover group-hover:opacity-80 transition"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center text-faint">
                    <ImageIcon size={32} />
                  </div>
                )}
                <span
                  className={`absolute top-2 left-2 rounded-full px-2 py-0.5 text-xs font-medium ${
                    b.isActive && b.emVigencia
                      ? 'bg-success-soft/80 text-success'
                      : 'bg-canvas/80 text-fg-2'
                  }`}
                >
                  {b.isActive ? (b.emVigencia ? 'Ativo' : 'Fora da vigência') : 'Inativo'}
                </span>
              </button>

              {/* Infos */}
              <div className="p-4 flex-1 flex flex-col gap-2">
                <div>
                  <h3 className="font-semibold text-fg truncate">{b.title}</h3>
                  {b.subtitle && (
                    <p className="text-xs text-muted truncate">{b.subtitle}</p>
                  )}
                </div>

                <div className="flex items-center gap-3 text-xs text-fg-2">
                  <span className="flex items-center gap-1">
                    <Eye size={12} /> {b.views}
                  </span>
                  <span className="flex items-center gap-1">
                    <MousePointerClick size={12} /> {b.clicks}
                  </span>
                  <span>
                    {formatDate(b.startDate)} → {formatDate(b.endDate)}
                  </span>
                </div>

                <div className="flex flex-wrap gap-1">
                  {b.cities.length === 0 ? (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-hover text-fg-2">
                      Todas as cidades
                    </span>
                  ) : (
                    b.cities.map((c) => (
                      <span
                        key={c}
                        className="text-xs px-2 py-0.5 rounded-full bg-primary-soft/30 text-primary border border-primary-line/50 flex items-center gap-1"
                      >
                        <MapPin size={10} /> {c}
                      </span>
                    ))
                  )}
                </div>

                {/* Ações */}
                <div className="mt-auto pt-2 flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => void handleToggle(b)}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-xs transition ${
                      b.isActive
                        ? 'bg-success-soft/30 text-success border border-success-line/50 hover:bg-success-soft/50'
                        : 'bg-canvas text-fg-2 border border-line hover:bg-hover'
                    }`}
                  >
                    <Power size={12} />
                    {b.isActive ? 'Ativo' : 'Inativo'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setStatsFor(b)}
                    className="px-2 py-1.5 rounded-lg bg-canvas border border-line text-fg-2 hover:bg-hover transition"
                    title="Estatísticas"
                  >
                    <BarChart3 size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditing(b)}
                    className="px-2 py-1.5 rounded-lg bg-canvas border border-line text-fg-2 hover:bg-hover transition"
                    title="Editar"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleDelete(b)}
                    className="px-2 py-1.5 rounded-lg bg-canvas border border-line text-danger hover:bg-danger-soft/30 transition"
                    title="Excluir"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <BannerFormModal
          banner={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      )}

      {statsFor && <BannerStatsModal banner={statsFor} onClose={() => setStatsFor(null)} />}
    </div>
  );
}

// ============================================================
// Form modal (criar/editar)
// ============================================================

function BannerFormModal({
  banner,
  onClose,
  onSaved,
}: {
  banner: Banner | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState(banner?.title ?? '');
  const [subtitle, setSubtitle] = useState(banner?.subtitle ?? '');
  const [imageUrl, setImageUrl] = useState(banner?.imageUrl ?? '');
  const [startDate, setStartDate] = useState(
    banner ? toDateInput(banner.startDate) : toDateInput(new Date().toISOString()),
  );
  const [endDate, setEndDate] = useState(
    banner
      ? toDateInput(banner.endDate)
      : toDateInput(new Date(Date.now() + 30 * 86400_000).toISOString()),
  );
  const [priority, setPriority] = useState(banner?.priority ?? 1);
  const [cities, setCities] = useState<string[]>(banner?.cities ?? []);
  const [cityInput, setCityInput] = useState('');
  const [detailTitle, setDetailTitle] = useState(banner?.detail?.title ?? '');
  const [detailBody, setDetailBody] = useState(banner?.detail?.body ?? '');
  const [detailFooter, setDetailFooter] = useState(banner?.detail?.footer ?? '');

  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  function addCity() {
    const c = cityInput.trim().toUpperCase();
    if (c && !cities.includes(c)) setCities([...cities, c]);
    setCityInput('');
  }

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    if (file.size > 5 * 1024 * 1024) {
      setError('Imagem grande demais (máx 5 MB).');
      return;
    }
    setUploading(true);
    try {
      const r = await bannersAPI.uploadImage(file);
      setImageUrl(r.url);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function handleSave() {
    if (!title.trim() || !imageUrl.trim()) {
      setError('Título e imagem são obrigatórios.');
      return;
    }
    setSaving(true);
    setError(null);
    const input: BannerInput = {
      title: title.trim(),
      subtitle: subtitle.trim() || undefined,
      imageUrl: imageUrl.trim(),
      priority,
      startDate: `${startDate}T00:00:00.000Z`,
      endDate: `${endDate}T23:59:59.999Z`,
      cities,
      detail: {
        title: detailTitle.trim() || undefined,
        body: detailBody.trim() || undefined,
        footer: detailFooter.trim() || undefined,
      },
    };
    try {
      if (banner) await bannersAPI.update(banner.id, input);
      else await bannersAPI.create(input);
      onSaved();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  const previewSrc = imageUrl ? (resolveAssetUrl(imageUrl) ?? imageUrl) : null;

  // Esc fecha; Enter salva; clique fora NÃO fecha (Fase 30)
  useModalKeys(onClose, () => void handleSave());

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl border border-line bg-surface p-6">
        <div className="mb-5 flex items-center justify-between">
          <h2 className="text-lg font-bold text-fg">
            {banner ? 'Editar banner' : 'Novo banner'}
          </h2>
          <button onClick={onClose} className="text-fg-2 hover:text-fg">
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4">
          {/* Imagem */}
          <div>
            <Label>Imagem do banner</Label>
            <div className="flex items-start gap-3">
              <div className="w-40 aspect-[2/1] rounded-lg bg-canvas border border-line overflow-hidden flex items-center justify-center flex-shrink-0">
                {previewSrc ? (
                  <img src={previewSrc} alt="" className="h-full w-full object-cover" />
                ) : (
                  <ImageIcon size={22} className="text-faint" />
                )}
              </div>
              <div className="flex-1 space-y-2">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={handleUpload}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="flex items-center gap-1.5 px-3 py-2 bg-primary-solid hover:bg-primary-solid-hover disabled:opacity-50 text-white rounded-lg text-sm transition"
                >
                  {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                  {uploading ? 'Enviando…' : 'Upload de imagem'}
                </button>
                <input
                  type="text"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  placeholder="ou cole uma URL de imagem"
                  className={inputCls}
                />
                <p className="text-xs text-muted">
                  Recomendado: 1200×600 px (proporção 2:1). PNG/JPG/WEBP até 5 MB.
                </p>
              </div>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label>Título (card no app)</Label>
              <input value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} placeholder="Ex: Promoção de Natal" />
            </div>
            <div>
              <Label>Subtítulo</Label>
              <input value={subtitle} onChange={(e) => setSubtitle(e.target.value)} className={inputCls} placeholder="opcional" />
            </div>
            <div>
              <Label>Início da vigência</Label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={inputCls} />
            </div>
            <div>
              <Label>Fim da vigência</Label>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={inputCls} />
            </div>
            <div>
              <Label>Prioridade (1–10, maior aparece primeiro)</Label>
              <input
                type="number"
                min={1}
                max={10}
                value={priority}
                onChange={(e) => setPriority(Math.max(1, Math.min(10, Number(e.target.value) || 1)))}
                className={inputCls}
              />
            </div>
          </div>

          {/* Cidades */}
          <div>
            <Label>Cidades que verão o banner (vazio = todas)</Label>
            <div className="flex gap-2">
              <input
                value={cityInput}
                onChange={(e) => setCityInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addCity();
                  }
                }}
                placeholder="Digite a cidade e Enter"
                className={`${inputCls} uppercase`}
              />
              <button
                type="button"
                onClick={addCity}
                className="px-3 py-2 bg-hover hover:bg-active text-fg rounded-lg text-sm transition"
              >
                <Plus size={14} />
              </button>
            </div>
            {cities.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {cities.map((c) => (
                  <span
                    key={c}
                    className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-primary-soft/30 text-primary border border-primary-line/50"
                  >
                    <MapPin size={10} /> {c}
                    <button
                      type="button"
                      onClick={() => setCities(cities.filter((x) => x !== c))}
                      className="ml-0.5 hover:text-primary"
                    >
                      <X size={11} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Página de detalhe */}
          <div className="rounded-xl border border-line bg-canvas p-4 space-y-3">
            <p className="text-sm font-semibold text-fg">
              Página de detalhe no app
            </p>
            <p className="text-xs text-muted -mt-2">
              Quando o cliente clica no banner, abre uma tela com este conteúdo.
            </p>
            <div>
              <Label>Título da página</Label>
              <input value={detailTitle} onChange={(e) => setDetailTitle(e.target.value)} className={inputCls} placeholder="Ex: Ganhe 3 meses de desconto!" />
            </div>
            <div>
              <Label>Corpo</Label>
              <textarea
                value={detailBody}
                onChange={(e) => setDetailBody(e.target.value)}
                rows={5}
                className={inputCls}
                placeholder="Descrição completa da promoção, condições, como participar…"
              />
            </div>
            <div>
              <Label>Rodapé</Label>
              <input value={detailFooter} onChange={(e) => setDetailFooter(e.target.value)} className={inputCls} placeholder="Ex: Válido até 31/12. Consulte condições." />
            </div>
          </div>

          {error && (
            <div className="px-4 py-3 rounded-lg bg-danger-soft/20 border border-danger-line/50 text-sm text-danger">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-hover hover:bg-active text-fg rounded-lg text-sm transition"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saving || uploading}
              className="flex items-center gap-2 px-4 py-2 bg-primary-solid hover:bg-primary-solid-hover disabled:opacity-50 text-white rounded-lg text-sm font-medium transition"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              {saving ? 'Salvando…' : banner ? 'Salvar alterações' : 'Criar banner'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Stats modal
// ============================================================

function BannerStatsModal({ banner, onClose }: { banner: Banner; onClose: () => void }) {
  const [stats, setStats] = useState<BannerStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    bannersAPI
      .stats(banner.id)
      .then(setStats)
      .catch((err) => setError(errorMessage(err)))
      .finally(() => setLoading(false));
  }, [banner.id]);

  // Esc fecha; clique fora NÃO fecha (Fase 30)
  useModalKeys(onClose);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl border border-line bg-surface p-6">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-fg">Estatísticas</h2>
            <p className="text-sm text-fg-2 truncate">{banner.title}</p>
          </div>
          <button onClick={onClose} className="text-fg-2 hover:text-fg">
            <X size={20} />
          </button>
        </div>

        {loading ? (
          <div className="py-12 text-center text-fg-2">
            <Loader2 size={22} className="mx-auto animate-spin" />
          </div>
        ) : error ? (
          <div className="px-4 py-3 rounded-lg bg-danger-soft/20 border border-danger-line/50 text-sm text-danger">
            {error}
          </div>
        ) : stats ? (
          <>
            <div className="mb-5 grid grid-cols-3 gap-3">
              <StatCard icon={<Eye size={16} />} label="Visualizações" value={stats.views} />
              <StatCard
                icon={<MousePointerClick size={16} />}
                label="Cliques"
                value={stats.clicks}
              />
              <StatCard icon={<BarChart3 size={16} />} label="CTR" value={`${stats.ctr}%`} />
            </div>

            {stats.viewers.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted">
                Nenhuma visualização registrada ainda.
              </p>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-line">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-canvas text-left text-xs uppercase tracking-wide text-muted">
                      <th className="px-3 py-2.5">Cliente (CPF/CNPJ)</th>
                      <th className="px-3 py-2.5">Cidade</th>
                      <th className="px-3 py-2.5">Dispositivo</th>
                      <th className="px-3 py-2.5">Visualizou</th>
                      <th className="px-3 py-2.5">Clicou</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line">
                    {stats.viewers.map((v) => (
                      <tr key={v.cpfCnpj} className="text-fg-2">
                        <td className="px-3 py-2 font-mono text-xs">{maskCpf(v.cpfCnpj)}</td>
                        <td className="px-3 py-2 text-xs">
                          {v.cidade ?? '—'}
                          {v.bairro ? ` · ${v.bairro}` : ''}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          <span className="flex items-center gap-1">
                            <Smartphone size={11} className="text-muted" />
                            {v.deviceModel ?? v.platform ?? '—'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-xs">{formatDateTime(v.viewedAt)}</td>
                        <td className="px-3 py-2 text-xs">
                          {v.clicked ? (
                            <span className="text-success">
                              {v.clickedAt ? formatDateTime(v.clickedAt) : 'Sim'}
                            </span>
                          ) : (
                            <span className="text-faint">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | string;
}) {
  return (
    <div className="rounded-xl border border-line bg-canvas p-3">
      <div className="flex items-center gap-1.5 text-xs text-muted">
        {icon} {label}
      </div>
      <div className="mt-1 text-xl font-bold text-fg">{value}</div>
    </div>
  );
}

// ============================================================
// Shared bits
// ============================================================

const inputCls =
  'w-full px-3 py-2 bg-surface border border-line rounded-lg text-fg text-sm placeholder-slate-600 focus:border-primary focus:outline-none';

function Label({ children }: { children: React.ReactNode }) {
  return (
    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted">
      {children}
    </label>
  );
}
